import { isKey } from './_generics';
import { TypeOf } from './_types';
/**
 * Typeguard used to validate {@link e} is an instance of type {@link Error}.
 * @param {unknown} e Type to guard
 * @returns {e is Error} True if e is an error instance.
 */
export const isError = (e: unknown): e is Error =>
  !!e && typeof e === 'object' && 'message' in e && 'stack' in e;
/**
 * Generic typeguard that can be used to validate a value is a non-null object with a
 * known list of properties
 * @template T The object type to evaluate.
 * @param {unknown} input The value to evaluate
 * @param {Array<[keyof T, TypeOf<T[keyof T]>]>} properties The properties to check for
 * @param {boolean} allRequired If true, all properties must be present
 * @returns {input is T} True if the provided value is a valid object with the specified properties.
 */
export const isGenericObjectType = <T>(
  input: unknown,
  properties: Array<[keyof T, TypeOf<T[keyof T]>]>,
  allRequired: number | boolean = true
): input is T => {
  let numMatches: number;
  if (typeof allRequired === 'boolean') {
    numMatches = allRequired ? properties.length : 1;
  } else if (typeof allRequired === 'number') {
    numMatches = allRequired;
  } else {
    numMatches = properties.length;
  }
  if (typeof input !== 'object' || !input) {
    return false;
  }
  const checkItem = (propArray: [keyof T, TypeOf<T[keyof T]>]) => {
    if (isKey(input, propArray[0])) {
      if (propArray[1] === 'unknown') {
        return true;
      }
      const prop = input[propArray[0]];
      if (typeof prop === propArray[1]) {
        return true;
      }
      if (propArray[1] === 'array') {
        return Array.isArray(prop);
      }
    }
    // If I made it this far I am not a match
    return false;
  };
  if (numMatches === 1) {
    // Otherwise a single match will do
    const foundIndex = properties.findIndex(checkItem);
    return foundIndex > -1;
  }
  // Loop until we find a non-match or hit the number requested
  for (let i = 0; i < Math.min(numMatches, properties.length); i++) {
    if (!checkItem(properties[i])) {
      return false;
    }
  }
  return true;
};
